'use client'

import { useMemo, useState } from 'react'
import Image from 'next/image'
import { cn } from '@desoutter/uikit'
import { LanguageRecord } from '@/graphql/types'
import { useParams } from 'next/navigation'
import { DocumentCategory } from '@/app/api/types/redpim.types'

type SearchResult = {
	id: string
	title: React.ReactNode
	bodyExcerpt: React.ReactNode
	url: string
}

export type DisplayResult = SearchResult & {
	imageSrc?: string
}

export default function ResultsView({
	data,
	products,
	dt,
	resources,
	count,
	translations,
}: {
	data: SearchResult[]
	products: DisplayResult[]
	dt: {
		serialNumber: string
		partNumber: string
		family: string
		name: string
	} | null
	resources?: {
		itemPath: string
		documentCategory: DocumentCategory
		metadataStorageName: string
		metadataStorageSize: string
	}[]
	count: number
	translations: LanguageRecord
}) {
	const { lang } = useParams()
	const allSiteSearchResults = (data || [])
		.filter((page) => !page.url.includes('/articles/'))
		.filter((page) => !page.url.includes('/products/series/'))
		.filter((page) => !page.url.includes('/solutions/'))
		.map((page) => ({
			...page,
			imageSrc: '/images/book-1.svg',
		})) as DisplayResult[]

	const procuctSearchResults = (data || [])
		.filter((product) => product.url.includes('/products/series/'))
		.map((product) => ({
			...product,
			imageSrc: '/images/box.svg',
		})) as DisplayResult[]

	const solutionSearchResults = (data || [])
		.filter((product) => product.url.includes('/solutions/'))
		.map((product) => ({
			...product,
			imageSrc: '/images/box.svg',
		})) as DisplayResult[]

	const articles = (data || [])
		.filter((article) => article.url.includes('/articles/'))
		.map((article) => ({
			...article,
			imageSrc: '/images/book.svg',
		})) as DisplayResult[]

	const documents = resources.map((resource) => ({
		id: resource.itemPath,
		title: `${translations.download} ${resource.metadataStorageName
			.split('.')
			.shift()}`,
		bodyExcerpt: `${resource.metadataStorageName
			.split('.')
			.pop()
			.toUpperCase()} · ${
			(Number(resource.metadataStorageSize) / (1024 * 1024)).toFixed(2) + ' MB'
		}`,
		imageSrc: '/images/document-download.svg',
		url: resource.itemPath,
		documentCategory: resource.documentCategory,
	}))

	const documentTypes = useMemo(() => {
		const categoryName = documents.map((doc) => {
			switch (doc.documentCategory) {
				case DocumentCategory.cadFiles:
					return translations.technicalDrawings
				case DocumentCategory.userManuals:
					return translations.userManuals
				case DocumentCategory.complianceCertificates:
					return translations.compliance
				case DocumentCategory.safetyInstructions:
					return translations.safetyInformations
				default:
					return 'Other'
			}
		})
		const types = Array.from(new Set(categoryName))
		return types
	}, [documents])

	const filters = useMemo(
		() => [
			{
				key: 'dt',
				label: 'Digital Twin',
				items: dt
					? [
							{
								id: 'dt',
								title: `Digital twin - ${dt.serialNumber}`,
								bodyExcerpt: `${dt.family} ${dt.name ? ' - ' + dt.name : ''}`,
								imageSrc: '/images/document-text.svg',
								url: `/digital-twin/${dt.partNumber}?sn=${dt.serialNumber}`,
							},
						]
					: [],
			},
			{
				key: 'products',
				label: translations.productsSolutions,
				items: [...products, ...procuctSearchResults],
			},
			{
				key: 'solutions',
				label: translations.solutions,
				items: solutionSearchResults,
			},
			{
				key: 'articles',
				label: translations.articles,
				items: articles,
			},
			{
				key: 'resources',
				label: translations.resources,
				items: documents,
			},
			{
				key: 'pages',
				label: translations.otherPages,
				items: allSiteSearchResults,
			},
		],
		[
			products,
			procuctSearchResults,
			articles,
			documents,
			allSiteSearchResults,
			dt,
		],
	)

	const [selected, setSelected] = useState('all')

	function handleURL(url: string) {
		const langPrefix = `/${lang}/`
		return url.includes(langPrefix) || url.startsWith('http')
			? url
			: `/${lang}${url}`
	}

	return (
		<div className="mt-3">
			<div className="flex flex-wrap ml-6 mb-3 text-gray-300 gap-1">
				{[{ key: 'all', label: translations.all, items: [] }, ...filters].map(
					(filter) => (
						<button
							className={cn(
								'btn pl-2 pr-0.5 py-0.5 border font-medium',
								filter.key === selected
									? 'bg-gray-50 border-gray-50 border text-gray-900'
									: 'border-gray-100 text-gray-600',
								{
									'bg-gray-100 text-gray-400':
										filter.key !== 'all' && filter.items.length === 0,
								},
							)}
							disabled={filter.key !== 'all' && filter.items.length === 0}
							onClick={() => setSelected(filter.key)}
							key={filter.key}
						>
							<div className="text-xs">{filter.label} </div>
							<div className="w-5 text-sm rounded-full bg-gray-100">
								{filter.key === 'all' ? count : filter.items.length}
							</div>
						</button>
					),
				)}
			</div>

			<div
				className={cn({
					'h-[360px] mr-5 overflow-y-auto scrollbar scrollbar-thumb-primary scrollbar-track-gray-100 scrollbar-w-1 scrollbar-thumb-rounded-full scrollbar-track-rounded-full':
						selected == 'all'
							? count > 5
							: filters.find((filter) => filter.key === selected).items.length >
								5,
				})}
			>
				{selected != 'resources' &&
					filters.map((result) => (
						<>
							{selected === 'all' && !!result.items.length && (
								<div className="mt-5 mb-3 px-6 text-xs text-gray-400">
									{result.label}
								</div>
							)}
							{(result.items as DisplayResult[]).map(
								(item, i) =>
									(selected === 'all' || selected === result.key) && (
										<a href={handleURL(item.url)} key={i}>
											<div className="mx-2.5 px-3 my-0.5 pb-2 rounded-2xl text-gray-900 hover:bg-gray-50">
												<div className="flex gap-3 items-start">
													<div className="w-10">
														<Image
															src={item.imageSrc}
															alt=""
															className="mt-1"
															width="44"
															height="44"
														/>
													</div>
													<div className="w-96">
														<div className="mb-1 font-medium">{item.title}</div>
														<div className="text-xs">{item.bodyExcerpt}</div>
													</div>
												</div>
											</div>
										</a>
									),
							)}
						</>
					))}

				{selected === 'resources' &&
					documentTypes.map((result) => {
						return (
							<>
								<div className="mt-5 mb-3 px-6 text-xs text-gray-400">
									{result}
								</div>
								{documents.map((doc, i) => (
									<a href={handleURL(doc.url)} key={i}>
										<div className="mx-2.5 px-3 my-0.5 pb-2 rounded-2xl text-gray-900 hover:bg-gray-50">
											<div className="flex gap-3 items-start">
												<div className="w-10">
													<Image
														src={doc.imageSrc}
														alt=""
														className="mt-1"
														width="44"
														height="44"
													/>
												</div>
												<div className="w-96">
													<div className="mb-1 font-medium">{doc.title}</div>
													<div className="text-xs">{doc.bodyExcerpt}</div>
												</div>
											</div>
										</div>
									</a>
								))}
							</>
						)
					})}
			</div>
		</div>
	)
}
